import React, { useEffect, useState } from 'react';
import { relationshipsApi } from '../api/client';
import type { Relationships as RelationshipsType, Collaborator, Mentor } from '../api/client';
import './Relationships.css';

const Relationships: React.FC = () => {
  const [relationships, setRelationships] = useState<RelationshipsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newRelationship, setNewRelationship] = useState({
    name: '',
    relationshipType: '',
    notes: '',
    influence: '',
    type: 'collaborator' as 'collaborator' | 'mentor' | 'trustedPeer'
  });

  useEffect(() => {
    fetchRelationships();
  }, []);

  const fetchRelationships = async () => {
    try {
      setLoading(true);
      const response = await relationshipsApi.getRelationships();
      setRelationships(response.data);
    } catch (err) {
      setError('Failed to load relationships');
      console.error('Relationships error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRelationship = async () => {
    if (!newRelationship.name.trim()) return;

    try {
      const relationshipData = {
        name: newRelationship.name,
        relationshipType: newRelationship.relationshipType,
        notes: newRelationship.notes,
        influence: newRelationship.influence,
        type: newRelationship.type
      };

      const response = await relationshipsApi.addRelationship(relationshipData);
      setRelationships(response.data);
      setShowAddModal(false);
      resetNewRelationship();
    } catch (err) {
      console.error('Error adding relationship:', err);
      setError('Failed to add relationship');
    }
  };

  const resetNewRelationship = () => {
    setNewRelationship({
      name: '',
      relationshipType: '',
      notes: '',
      influence: '',
      type: 'collaborator'
    });
  };

  const getRelationshipIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'professional colleague': return '💼';
      case 'creative collaborator': return '🎨';
      case 'mentor': return '🧭';
      case 'friend': return '👥';
      case 'family': return '👨‍👩‍👧‍👦';
      default: return '🤝';
    }
  };

  if (loading) {
    return <div className="relationships-page"><div className="loading">Loading relationships...</div></div>;
  }

  if (error) {
    return <div className="relationships-page"><div className="error">Error: {error}</div></div>;
  }

  return (
    <div className="relationships-page">
      <header className="page-header">
        <h1>🤝 Relationships</h1>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowAddModal(true)}
        >
          Add New Relationship
        </button>
      </header>

      {error && <div className="error-message">{error}</div>}

      <div className="relationships-content">
        {/* Collaborators Section */}
        <div className="relationships-section">
          <div className="section-header">
            <h2>💼 Collaborators</h2>
            <span className="count-badge">{relationships?.collaborators?.length || 0}</span>
          </div>
          <div className="relationships-grid">
            {relationships?.collaborators?.map((collaborator, index) => (
              <div key={index} className="relationship-card collaborator">
                <div className="relationship-header">
                  <span className="relationship-icon">{getRelationshipIcon(collaborator.relationshipType)}</span>
                  <h3 className="relationship-name">{collaborator.name}</h3>
                </div>
                <div className="relationship-content">
                  <div className="relationship-type">
                    <span className="type-badge">{collaborator.relationshipType}</span>
                  </div>
                  {collaborator.notes && (
                    <p className="relationship-notes">{collaborator.notes}</p>
                  )}
                </div>
              </div>
            ))}
            {(!relationships?.collaborators || relationships.collaborators.length === 0) && (
              <div className="no-relationships">No collaborators added yet</div>
            )}
          </div>
        </div>

        {/* Mentors Section */}
        <div className="relationships-section">
          <div className="section-header">
            <h2>🧭 Mentors</h2>
            <span className="count-badge">{relationships?.mentors?.length || 0}</span>
          </div>
          <div className="relationships-grid">
            {relationships?.mentors?.map((mentor, index) => (
              <div key={index} className="relationship-card mentor">
                <div className="relationship-header">
                  <span className="relationship-icon">🧭</span>
                  <h3 className="relationship-name">{mentor.name}</h3>
                </div>
                <div className="relationship-content">
                  <div className="relationship-type">
                    <span className="type-badge mentor">Mentor</span>
                  </div>
                  {mentor.influence && (
                    <div className="influence-section">
                      <span className="influence-label">Influence:</span>
                      <p className="influence-text">{mentor.influence}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {(!relationships?.mentors || relationships.mentors.length === 0) && (
              <div className="no-relationships">No mentors added yet</div>
            )}
          </div>
        </div>

        {/* Trusted Peers Section */}
        <div className="relationships-section">
          <div className="section-header">
            <h2>👥 Trusted Peers</h2>
            <span className="count-badge">{relationships?.trustedPeers?.length || 0}</span>
          </div>
          <div className="trusted-peers-list">
            {relationships?.trustedPeers?.map((peer, index) => (
              <div key={index} className="trusted-peer-item">
                <span className="peer-icon">👤</span>
                <span className="peer-name">{peer}</span>
              </div>
            ))}
            {(!relationships?.trustedPeers || relationships.trustedPeers.length === 0) && (
              <div className="no-relationships">No trusted peers added yet</div>
            )}
          </div>
        </div>
      </div>

      {/* Add Relationship Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Relationship</h3>
              <button 
                className="modal-close"
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Relationship Type</label>
                <select 
                  value={newRelationship.type}
                  onChange={(e) => setNewRelationship({...newRelationship, type: e.target.value as 'collaborator' | 'mentor' | 'trustedPeer'})}
                  className="form-select"
                >
                  <option value="collaborator">Collaborator</option>
                  <option value="mentor">Mentor</option>
                  <option value="trustedPeer">Trusted Peer</option>
                </select>
              </div>

              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={newRelationship.name}
                  onChange={(e) => setNewRelationship({...newRelationship, name: e.target.value})}
                  className="form-input"
                  placeholder="Enter name..."
                />
              </div>

              {newRelationship.type !== 'trustedPeer' && (
                <>
                  <div className="form-group">
                    <label>Relationship Type/Role</label>
                    <input
                      type="text"
                      value={newRelationship.relationshipType}
                      onChange={(e) => setNewRelationship({...newRelationship, relationshipType: e.target.value})}
                      className="form-input"
                      placeholder="e.g., Professional Colleague, Creative Collaborator..."
                    />
                  </div>

                  {newRelationship.type === 'collaborator' && (
                    <div className="form-group">
                      <label>Notes</label>
                      <textarea
                        value={newRelationship.notes}
                        onChange={(e) => setNewRelationship({...newRelationship, notes: e.target.value})}
                        className="form-textarea"
                        rows={3}
                        placeholder="Notes about this collaboration..."
                      />
                    </div>
                  )}

                  {newRelationship.type === 'mentor' && (
                    <div className="form-group">
                      <label>Influence</label>
                      <textarea
                        value={newRelationship.influence}
                        onChange={(e) => setNewRelationship({...newRelationship, influence: e.target.value})}
                        className="form-textarea"
                        rows={3}
                        placeholder="Describe their influence on you..."
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <button 
                className="btn btn-success"
                onClick={handleAddRelationship}
                disabled={!newRelationship.name.trim()}
              >
                Add Relationship
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Relationships;